/**
 * ADMIN-T010 — runtime config loader.
 *
 * Merge order (later wins): defaults → build baseline → GET /admin-bff/runtime-config.
 * Unknown server keys are kept verbatim (forward compat); missing critical keys throw
 * a `BootstrapFailed`-style error so the bootstrap state machine can surface the failure.
 *
 * SPEC §6.4 Cheat Sheet — the explicit field set tracked here.
 */

import { BUILD_CONFIG, type BuildConfig } from './buildConfig';
import { ERROR_CODES } from '@/observability/errorCodes';

export interface RuntimeConfig extends BuildConfig {
  api: {
    coreBaseUrl: string;
    biBaseUrl: string;
  };
  features: Record<string, boolean>;
  polling: {
    exportJobMs: number;
    kpiSnapshotMs: number;
    notificationMs: number;
    [k: string]: number;
  };
  branding: Record<string, string>;
  permissionVersion?: string;
  dictionaryVersion?: string;
}

/**
 * Defaults explicitly omit `api.coreBaseUrl` / `api.biBaseUrl`: these are required and
 * must come from the build baseline or runtime config. Missing them is a bootstrap failure.
 */
const DEFAULTS = {
  features: {
    mockApi: false,
    debugPanel: false,
  },
  polling: {
    exportJobMs: 3000,
    kpiSnapshotMs: 30_000,
    notificationMs: 15_000,
  },
  branding: {
    appName: '力聚售后管理后台',
    themeKey: 'default',
  },
} as const;

export class RuntimeConfigLoadError extends Error {
  override readonly name = 'RuntimeConfigLoadError';
  readonly code = ERROR_CODES.runtime_config_load_error;
  readonly reason?: unknown;
  constructor(message: string, reason?: unknown) {
    super(message);
    this.reason = reason;
  }
}

/** Thrown internally when the server returns 404 — treated as "no server override". */
class RuntimeConfigNotFound extends Error {
  override readonly name = 'RuntimeConfigNotFound';
}

export class RuntimeConfigInvalid extends Error {
  override readonly name = 'RuntimeConfigInvalid';
  readonly code = ERROR_CODES.bootstrap_failed;
  constructor(readonly missingFields: string[]) {
    super(`runtime config missing required fields: ${missingFields.join(', ')}`);
  }
}

function deepMerge(
  base: Record<string, unknown>,
  override: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!override) return { ...base };
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(override)) {
    const existing = out[k];
    if (
      v !== null &&
      typeof v === 'object' &&
      !Array.isArray(v) &&
      existing !== null &&
      typeof existing === 'object' &&
      !Array.isArray(existing)
    ) {
      out[k] = deepMerge(
        existing as Record<string, unknown>,
        v as Record<string, unknown>,
      );
    } else if (v !== undefined) {
      out[k] = v;
    }
  }
  return out;
}

function assertRequired(cfg: RuntimeConfig): void {
  const missing: string[] = [];
  if (!cfg.api?.coreBaseUrl) missing.push('api.coreBaseUrl');
  if (!cfg.api?.biBaseUrl) missing.push('api.biBaseUrl');
  if (missing.length) throw new RuntimeConfigInvalid(missing);
}

export interface LoadRuntimeConfigDeps {
  fetchJson?: (url: string) => Promise<unknown>;
  buildBaseline?: BuildConfig;
  endpoint?: string;
}

const defaultFetchJson = async (url: string): Promise<unknown> => {
  const res = await fetch(url, { credentials: 'include' });
  // 404 = server doesn't provide dynamic config; caller treats as soft miss.
  if (res.status === 404) throw new RuntimeConfigNotFound();
  if (!res.ok) throw new Error(`HTTP ${res.status} loading ${url}`);
  return res.json();
};

export async function loadRuntimeConfig(
  deps: LoadRuntimeConfigDeps = {},
): Promise<RuntimeConfig> {
  const baseline = deepMerge(
    DEFAULTS as unknown as Record<string, unknown>,
    (deps.buildBaseline ?? BUILD_CONFIG) as Record<string, unknown>,
  ) as unknown as RuntimeConfig;
  const endpoint = deps.endpoint ?? `${baseline.api.coreBaseUrl}/runtime-config`;
  let serverConfig: Record<string, unknown> | undefined;
  try {
    const json = await (deps.fetchJson ?? defaultFetchJson)(endpoint);
    if (json && typeof json === 'object') {
      serverConfig = json as Record<string, unknown>;
    }
  } catch (err) {
    serverConfig = undefined;
    // 404 = server doesn't implement /runtime-config yet — use build config only.
    // Any other failure is fatal unless MSW mock mode is active.
    if (!baseline.features?.mockApi && !(err instanceof RuntimeConfigNotFound)) {
      throw new RuntimeConfigLoadError('failed to load /runtime-config', err);
    }
  }
  const merged = deepMerge(
    baseline as unknown as Record<string, unknown>,
    serverConfig,
  ) as unknown as RuntimeConfig;
  assertRequired(merged);
  return merged;
}
